import React, { useState, useEffect } from "react";
import { useDirection } from "../contexts/DirectionContext";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  SpaceDashboard as DashboardIcon,
  LocalLibrary as MenuBookIcon,
  Category as CategoryIcon,
  Group as PeopleIcon,
  SettingsSuggest as SettingsIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  MenuBook as LibraryIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  OndemandVideo as VideoIcon,
  Logout as LogoutIcon,
  KeyboardArrowDown as ArrowDownIcon,
  AutoStories as AutoStoriesIcon,
} from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import LanguageSwitcher from "../components/common/LanguageSwitcher";
import { categoriesAPI } from "../services/api";

const DRAWER_OPEN = 260;
const DRAWER_MINI = 68;
const APPBAR_H = 64;
const TICKER_H = 34;
const TOP_OFFSET = APPBAR_H + TICKER_H;

const getLocalizedContent = (content, language) => {
  if (!content) return "";
  const values = content.split("|");
  if (language === "fr") return values[1] || values[0] || "";
  if (language === "ar") return values[2] || values[0] || "";
  return values[0] || "";
};

// ─── Styled components ───────────────────────────────────────────────────────

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  height: APPBAR_H,
  zIndex: theme.zIndex.drawer + 1,
  backdropFilter: "blur(12px)",
  backgroundColor:
    theme.palette.mode === "light"
      ? "rgba(255,255,255,0.92)"
      : "rgba(15,15,25,0.88)",
  boxShadow:
    theme.palette.mode === "light"
      ? "0 1px 0 rgba(0,0,0,0.08)"
      : "0 1px 0 rgba(255,255,255,0.06)",
  borderBottom: `1px solid ${
    theme.palette.mode === "light"
      ? "rgba(220,220,230,0.6)"
      : "rgba(60,60,80,0.6)"
  }`,
  "& .MuiToolbar-root": {
    minHeight: APPBAR_H,
    height: APPBAR_H,
    padding: "0 20px",
  },
}));

const TickerBar = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: APPBAR_H,
  left: 0,
  right: 0,
  height: TICKER_H,
  zIndex: theme.zIndex.drawer + 1,
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  background:
    theme.palette.mode === "light"
      ? "linear-gradient(90deg, #0a3d7a 0%, #1565c0 100%)"
      : "linear-gradient(90deg, #0d1b3e 0%, #0a3d7a 100%)",
  borderBottom: `1px solid ${
    theme.palette.mode === "light"
      ? "rgba(0,0,0,0.15)"
      : "rgba(255,255,255,0.08)"
  }`,
}));

const DrawerPaper = styled(Box)(({ theme, open }) => ({
  position: "fixed",
  top: TOP_OFFSET,
  left: 0,
  height: `calc(100vh - ${TOP_OFFSET}px)`,
  width: open ? DRAWER_OPEN : DRAWER_MINI,
  overflowX: "hidden",
  overflowY: "auto",
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.easeInOut,
    duration: 220,
  }),
  background:
    theme.palette.mode === "light"
      ? "#ffffff"
      : "#0f0f1a",
  borderRight: `1px solid ${
    theme.palette.mode === "light"
      ? "rgba(0,0,0,0.07)"
      : "rgba(255,255,255,0.06)"
  }`,
  zIndex: theme.zIndex.drawer,
  display: "flex",
  flexDirection: "column",
  "&::-webkit-scrollbar": { width: 4 },
  "&::-webkit-scrollbar-thumb": {
    background:
      theme.palette.mode === "light"
        ? "rgba(0,0,0,0.12)"
        : "rgba(255,255,255,0.12)",
    borderRadius: 4,
  },
}));

const NavItem = styled(ListItem)(({ theme, active }) => ({
  borderRadius: 10,
  margin: "2px 8px",
  padding: "9px 12px",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  background: active
    ? theme.palette.mode === "light"
      ? alpha(theme.palette.primary.main, 0.1)
      : alpha(theme.palette.primary.main, 0.18)
    : "transparent",
  transition: theme.transitions.create(["background", "transform"], {
    duration: 160,
  }),
  "&::before": active
    ? {
        content: '""',
        position: "absolute",
        insetInlineStart: 0,
        top: "20%",
        height: "60%",
        width: 3,
        borderRadius: "0 3px 3px 0",
        backgroundColor: theme.palette.primary.main,
      }
    : {},
  "& .MuiListItemIcon-root": {
    minWidth: 36,
    color: active
      ? theme.palette.primary.main
      : theme.palette.mode === "light"
      ? "rgba(0,0,0,0.5)"
      : "rgba(255,255,255,0.45)",
    transition: "color 0.16s",
  },
  "& .MuiListItemText-primary": {
    fontSize: "0.875rem",
    fontWeight: active ? 600 : 500,
    color: active
      ? theme.palette.mode === "light"
        ? theme.palette.primary.dark
        : theme.palette.primary.light
      : theme.palette.mode === "light"
      ? "rgba(0,0,0,0.72)"
      : "rgba(255,255,255,0.65)",
    letterSpacing: "0.01em",
  },
  "&:hover": {
    background:
      theme.palette.mode === "light"
        ? "rgba(0,0,0,0.045)"
        : "rgba(255,255,255,0.05)",
    "& .MuiListItemIcon-root": {
      color: theme.palette.primary.main,
    },
  },
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color:
    theme.palette.mode === "light"
      ? "rgba(0,0,0,0.38)"
      : "rgba(255,255,255,0.3)",
  padding: "12px 20px 4px",
  fontFamily: "Inter, system-ui, sans-serif",
}));

const Main = styled("main")(({ theme, draweropen }) => ({
  flexGrow: 1,
  marginInlineStart: draweropen ? DRAWER_OPEN : DRAWER_MINI,
  marginTop: TOP_OFFSET,
  minHeight: `calc(100vh - ${TOP_OFFSET}px)`,
  background:
    theme.palette.mode === "light" ? "#f4f6fb" : "#0a0a14",
  transition: theme.transitions.create("margin-inline-start", {
    easing: theme.transitions.easing.easeInOut,
    duration: 220,
  }),
  padding: theme.spacing(3),
}));

// ─── Component ────────────────────────────────────────────────────────────────

const DashboardLayout = () => {
  const [open, setOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pinnedCategories, setPinnedCategories] = useState([]);
  const { t, i18n } = useTranslation();
  const { user, logout, isAdmin } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { direction } = useDirection();
  const navigate = useNavigate();
  const location = useLocation();
  const isRTL = direction === "rtl";

  useEffect(() => {
    categoriesAPI
      .getPinned()
      .then((r) => setPinnedCategories(r.data.categories || []))
      .catch(() => {});
  }, []);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
  };

  const menuItems = [
    { text: t("navigation.dashboard"), icon: <DashboardIcon sx={{ fontSize: 22 }} />, path: "/dashboard" },
    { text: t("navigation.books"), icon: <MenuBookIcon sx={{ fontSize: 22 }} />, path: "/books" },
    { text: t("navigation.videos"), icon: <VideoIcon sx={{ fontSize: 22 }} />, path: "/videos" },
  ];

  const adminMenuItems = [
    { text: t("navigation.adminDashboard"), icon: <AdminIcon sx={{ fontSize: 22 }} />, path: "/admin_dashboard" },
    { text: t("navigation.bookManagement"), icon: <LibraryIcon sx={{ fontSize: 22 }} />, path: "/admin/books_management" },
    { text: t("navigation.videoManagement"), icon: <VideoIcon sx={{ fontSize: 22 }} />, path: "/admin/videos_management" },
    { text: t("navigation.categories"), icon: <CategoryIcon sx={{ fontSize: 22 }} />, path: "/admin/categories" },
    { text: t("navigation.userManagement"), icon: <PeopleIcon sx={{ fontSize: 22 }} />, path: "/admin/users" },
    { text: t("navigation.settings"), icon: <SettingsIcon sx={{ fontSize: 22 }} />, path: "/admin/settings" },
  ];

  const renderNavItem = (item) => {
    const isActive = location.pathname === item.path;
    return (
      <Tooltip
        key={item.path}
        title={!open ? item.text : ""}
        placement={isRTL ? "left" : "right"}
      >
        <NavItem
          active={isActive ? 1 : 0}
          onClick={() => navigate(item.path)}
          sx={{
            justifyContent: open ? "flex-start" : "center",
            px: open ? "12px" : 0,
            width: open ? "auto" : 52,
            mx: open ? "8px" : "auto",
          }}
        >
          <ListItemIcon
            sx={{
              justifyContent: "center",
              minWidth: open ? 36 : "auto",
            }}
          >
            {item.icon}
          </ListItemIcon>
          {open && <ListItemText primary={item.text} />}
        </NavItem>
      </Tooltip>
    );
  };

  const userInitial = user?.first_name?.[0] || user?.username?.[0] || "U";
  const userDisplayName = user?.first_name
    ? `${user.first_name} ${user.last_name || ""}`.trim()
    : user?.username || "";

  return (
    <Box sx={{ display: "flex" }} dir={isRTL ? "rtl" : "ltr"}>
      {/* ── AppBar ─────────────────────────────────────────────────── */}
      <StyledAppBar position="fixed">
        <Toolbar>
          {/* Logo + toggle */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: open ? DRAWER_OPEN - 20 : DRAWER_MINI - 8,
              transition: "min-width 0.22s ease",
            }}
          >
            <IconButton
              onClick={() => setOpen((v) => !v)}
              size="small"
              sx={{
                color: "text.secondary",
                "&:hover": { color: "primary.main" },
              }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>

            {open && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
                onClick={() => navigate("/dashboard")}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    background: "linear-gradient(135deg, #0a3d7a 0%, #1565c0 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AutoStoriesIcon sx={{ fontSize: 18, color: "#fff" }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "1rem",
                    background: "linear-gradient(90deg, #0a3d7a, #1976d2)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: "-0.02em",
                    fontFamily: "Inter, system-ui, sans-serif",
                  }}
                >
                  eBiblio Space
                </Typography>
              </Box>
            )}
          </Box>

          {/* Center — page title placeholder */}
          <Box sx={{ flex: 1 }} />

          {/* Right controls */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <LanguageSwitcher label={t("profile.fields.language")} isAppbar />

            <Tooltip title={isDarkMode ? t("theme.light") : t("theme.dark")}>
              <IconButton
                onClick={toggleTheme}
                size="small"
                sx={{
                  color: "text.secondary",
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  "&:hover": {
                    background: (th) =>
                      th.palette.mode === "light"
                        ? "rgba(0,0,0,0.06)"
                        : "rgba(255,255,255,0.06)",
                  },
                }}
              >
                {isDarkMode ? (
                  <LightModeIcon fontSize="small" />
                ) : (
                  <DarkModeIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 0.5, my: 1.5, opacity: 0.3 }}
            />

            {/* Profile button */}
            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                borderRadius: "10px",
                px: 1,
                py: 0.5,
                transition: "background 0.15s",
                "&:hover": {
                  background: (th) =>
                    th.palette.mode === "light"
                      ? "rgba(0,0,0,0.05)"
                      : "rgba(255,255,255,0.06)",
                },
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #0a3d7a, #1565c0)",
                }}
              >
                {userInitial}
              </Avatar>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    color: "text.primary",
                    lineHeight: 1.2,
                  }}
                >
                  {userDisplayName}
                </Typography>
                {isAdmin && (
                  <Typography
                    sx={{
                      fontSize: "0.68rem",
                      color: "primary.main",
                      lineHeight: 1.2,
                      fontWeight: 500,
                    }}
                  >
                    Admin
                  </Typography>
                )}
              </Box>
              <ArrowDownIcon sx={{ fontSize: 16, color: "text.disabled" }} />
            </Box>
          </Box>
        </Toolbar>
      </StyledAppBar>

      {/* ── News ticker ────────────────────────────────────────────── */}
      <TickerBar>
        {/* LIVE badge */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.75,
            height: "100%",
            background: "rgba(0,0,0,0.18)",
            borderInlineEnd: "1px solid rgba(255,255,255,0.18)",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#4cff91",
              boxShadow: "0 0 8px #4cff91",
              animation: "blink 1.4s ease-in-out infinite",
              "@keyframes blink": {
                "0%,100%": { opacity: 1 },
                "50%": { opacity: 0.25 },
              },
            }}
          />
          <Typography
            sx={{
              color: "#fff",
              fontSize: "0.68rem",
              fontWeight: 900,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontFamily: "Inter, system-ui, sans-serif",
              userSelect: "none",
            }}
          >
            LIVE
          </Typography>
        </Box>

        {/* Scrolling welcome text — seamless infinite loop */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            position: "relative",
            // mask to fade edges
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)",
            maskImage:
              "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
              // For LTR: start offscreen right, move left
              // For RTL: start offscreen left, move right
              animation: isRTL
                ? "scrollRTL 22s linear infinite"
                : "scrollLTR 22s linear infinite",
              "@keyframes scrollLTR": {
                "0%":   { transform: "translateX(100vw)" },
                "100%": { transform: "translateX(-100%)" },
              },
              "@keyframes scrollRTL": {
                "0%":   { transform: "translateX(-100vw)" },
                "100%": { transform: "translateX(100%)" },
              },
            }}
          >
            {/* Repeat 3× for seamless feel */}
            {[0, 1, 2].map((i) => (
              <Typography
                key={i}
                component="span"
                sx={{
                  color: "rgba(255,255,255,0.95)",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  fontFamily: isRTL
                    ? '"Readex Pro","Noto Sans Arabic","Noto Kufi Arabic",sans-serif'
                    : "Inter, system-ui, sans-serif",
                  letterSpacing: isRTL ? 0 : "0.02em",
                  px: 6,
                  userSelect: "none",
                }}
              >
                {t("welcome")}
                <Box
                  component="span"
                  sx={{ mx: 3, opacity: 0.45, fontSize: "0.7rem" }}
                >
                  ✦
                </Box>
              </Typography>
            ))}
          </Box>
        </Box>
      </TickerBar>

      {/* ── Sidebar ────────────────────────────────────────────────── */}
      <DrawerPaper open={open}>
        {/* Main nav */}
        <List sx={{ pt: 1.5, px: 0 }} disablePadding>
          {open && <SectionLabel>Menu</SectionLabel>}
          {menuItems.map(renderNavItem)}
        </List>

        {/* Pinned categories */}
        {pinnedCategories.length > 0 && open && (
          <Box sx={{ mt: 1 }}>
            <SectionLabel>{t("navigation.pinnedCategories")}</SectionLabel>
            <Box sx={{ px: 2, display: "flex", flexWrap: "wrap", gap: 0.75, pb: 1 }}>
              {pinnedCategories.map((cat) => (
                <Chip
                  key={cat.id}
                  label={getLocalizedContent(cat.name, i18n.language)}
                  size="small"
                  onClick={() =>
                    navigate(`/books?category=${cat.id}`, {
                      state: { categoryId: cat.id },
                    })
                  }
                  sx={{
                    height: 26,
                    fontSize: "0.73rem",
                    fontWeight: 500,
                    cursor: "pointer",
                    background: (th) =>
                      th.palette.mode === "light"
                        ? "rgba(0,0,0,0.05)"
                        : "rgba(255,255,255,0.07)",
                    color: "text.secondary",
                    border: "none",
                    "&:hover": {
                      background: (th) =>
                        alpha(th.palette.primary.main, 0.12),
                      color: "primary.main",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Admin nav */}
        {isAdmin && (
          <>
            <Divider sx={{ mx: 2, my: 1, opacity: 0.4 }} />
            <List sx={{ px: 0, pb: 2 }} disablePadding>
              {open && <SectionLabel>Admin</SectionLabel>}
              {adminMenuItems.map(renderNavItem)}
            </List>
          </>
        )}

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />
      </DrawerPaper>

      {/* ── Profile menu ──────────────────────────────────────────── */}
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: isRTL ? "left" : "right", vertical: "top" }}
        anchorOrigin={{ horizontal: isRTL ? "left" : "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 4,
          sx: {
            borderRadius: 2,
            minWidth: 200,
            mt: 0.5,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            border: (th) =>
              `1px solid ${
                th.palette.mode === "light"
                  ? "rgba(0,0,0,0.07)"
                  : "rgba(255,255,255,0.07)"
              }`,
          },
        }}
      >
        {/* User info header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.875rem" }}>
            {userDisplayName}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {user?.email}
          </Typography>
        </Box>

        <MenuItem
          onClick={() => { navigate("/profile"); setAnchorEl(null); }}
          sx={{ gap: 1.5, py: 1.2, borderRadius: 1, mx: 0.5, mt: 0.5, fontSize: "0.875rem" }}
        >
          <PersonIcon fontSize="small" sx={{ color: "text.secondary" }} />
          {t("navigation.profile")}
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            gap: 1.5,
            py: 1.2,
            borderRadius: 1,
            mx: 0.5,
            mb: 0.5,
            fontSize: "0.875rem",
            color: "error.main",
            "&:hover": { background: (th) => alpha(th.palette.error.main, 0.08) },
          }}
        >
          <LogoutIcon fontSize="small" />
          {t("auth.logout")}
        </MenuItem>
      </Menu>

      {/* ── Main content ──────────────────────────────────────────── */}
      <Main draweropen={open ? 1 : 0}>
        <Box
          sx={{
            background: (th) =>
              th.palette.mode === "light" ? "#ffffff" : "#13131f",
            borderRadius: 3,
            minHeight: "calc(100vh - 96px)",
            p: { xs: 2.5, md: 4 },
            boxShadow: (th) =>
              th.palette.mode === "light"
                ? "0 1px 4px rgba(0,0,0,0.06)"
                : "0 1px 4px rgba(0,0,0,0.3)",
          }}
        >
          <Outlet />
        </Box>
      </Main>
    </Box>
  );
};

export default DashboardLayout;
